import { StyleSheet, TextStyle, View, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";

type BadgeProps = {
  style?: ViewStyle;
  textStyle?: TextStyle;
  labelStyle?: TextStyle;
  text: string;
  tone?:
    | "sky"
    | "indigo"
    | "emerald"
    | "green"
    | "amber"
    | "yellow"
    | "violet"
    | "red"
    | "slate"
    | "rose"
    | "purple"
    | "blue"
    | "gray"
    | "teal"
    | "cyan"
    | "ruby";
  label?: string;
  coloredLabel?: boolean;
  icon?: keyof typeof MaterialCommunityIcons.glyphMap;
  iconSize?: number;
};

const Badge = ({
  style,
  textStyle,
  labelStyle,
  text,
  tone = "slate",
  label,
  coloredLabel = false,
  icon,
  iconSize = 12,
}: BadgeProps) => {
  const { colors } = useTheme();
  const map: any = {
    sky: colors.sky,
    indigo: colors.indigo,
    emerald: colors.emerald,
    green: colors.green,
    amber: colors.amber,
    yellow: colors.yellow,
    violet: colors.violet,
    red: colors.red,
    slate: colors.slate,
    rose: colors.rose,
    purple: colors.purple,
    blue: colors.blue,
    gray: colors.gray,
    teal: colors.teal,
    cyan: colors.cyan,
    ruby: colors.ruby,
  };
  const c = map[tone] ?? colors.slate;

  // Extract borderRadius from style prop (like Button extracts backgroundColor)
  const getBorderRadius = (): number => {
    if (!style) return 12;
    const flattenedStyle = StyleSheet.flatten(style);
    return typeof flattenedStyle?.borderRadius === "number" ? flattenedStyle.borderRadius : 12;
  };
  const borderRadius = getBorderRadius();

  // Determine which part gets the colored styling
  const hasLabel = !!label || !!icon;
  const labelColored = coloredLabel && hasLabel;
  const textColored = !coloredLabel;

  return (
    <View style={[{ flexDirection: "row" }, style]}>
      {hasLabel ? (
        <View
          style={[
            {
              flexDirection: "row",
              alignItems: "center",
              borderWidth: StyleSheet.hairlineWidth,
              paddingHorizontal: 8,
              paddingVertical: 4,
              borderTopLeftRadius: borderRadius,
              borderBottomLeftRadius: borderRadius,
              ...(labelColored
                ? { backgroundColor: c.background, borderColor: c.border }
                : { borderColor: colors.border }),
            },
          ]}
        >
          {icon && (
            <MaterialCommunityIcons
              name={icon}
              size={iconSize}
              color={labelColored ? c.text : colors.text}
              style={label ? { marginRight: 4 } : undefined}
            />
          )}
          {label && (
            <ThemedText
              style={[
                {
                  fontSize: 11,
                  fontWeight: "600",
                  ...(labelColored ? { color: c.text } : {}),
                },
                textStyle,
                labelStyle,
              ]}
            >
              {label}
            </ThemedText>
          )}
        </View>
      ) : null}
      <ThemedText
        style={[
          {
            borderTopRightRadius: borderRadius,
            borderBottomRightRadius: borderRadius,
            borderBottomLeftRadius: !hasLabel ? borderRadius : undefined,
            borderTopLeftRadius: !hasLabel ? borderRadius : undefined,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderWidth: StyleSheet.hairlineWidth,
            fontSize: 11,
            fontWeight: "700",
            ...(textColored
              ? { backgroundColor: c.background, borderColor: c.border, color: c.text }
              : { borderColor: colors.border }),
          },
          textStyle,
        ]}
      >
        {text}
      </ThemedText>
    </View>
  );
};
export default Badge;
